import React from 'react';

// ── Donut Chart (SVG) ──
export function DonutChart({ data, colors, size = 160, strokeWidth = 28 }) {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  if (total === 0) return null;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  let offset = 0;
  const entries = Object.entries(data).filter(([, v]) => v > 0);

  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="donut-chart">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {entries.map(([key, val]) => {
          const pct = val / total;
          const dashArray = `${circumference * pct} ${circumference * (1 - pct)}`;
          const dashOffset = -offset * circumference;
          const segment = (
            <circle
              key={key}
              cx={center} cy={center} r={radius}
              fill="none"
              stroke={colors[key] || '#CBD5E1'}
              strokeWidth={strokeWidth}
              strokeDasharray={dashArray}
              strokeDashoffset={dashOffset}
              strokeLinecap="butt"
              transform={`rotate(-90 ${center} ${center})`}
              className="transition-all duration-500"
            />
          );
          offset += pct;
          return segment;
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-['Cabinet_Grotesk'] font-bold text-slate-900">{total}</span>
        <span className="text-[9px] text-slate-400 uppercase tracking-wider">Total</span>
      </div>
    </div>
  );
}

// ── Grouped Bar Chart (SVG) ──
export function GroupedBarChart({ data, height = 180 }) {
  // data: [{label, total, approved}]
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map(d => d.total), 1);
  const barWidth = Math.min(24, Math.max(12, Math.floor(500 / data.length / 3)));
  const gap = Math.min(40, Math.max(16, Math.floor(500 / data.length)));
  const chartWidth = data.length * gap + 40;
  const chartHeight = height;
  const padTop = 16;
  const padBottom = 24;
  const usableH = chartHeight - padTop - padBottom;

  return (
    <div className="overflow-x-auto" data-testid="grouped-bar-chart">
      <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map(pct => (
          <line key={pct} x1={20} y1={padTop + usableH * (1 - pct)} x2={chartWidth} y2={padTop + usableH * (1 - pct)}
            stroke="#F1F5F9" strokeWidth={1} />
        ))}
        {data.map((d, i) => {
          const x = 30 + i * gap;
          const totalH = (d.total / maxVal) * usableH;
          const approvedH = (d.approved / maxVal) * usableH;
          return (
            <g key={i}>
              {/* Total bar */}
              <rect x={x} y={padTop + usableH - totalH} width={barWidth} height={totalH}
                rx={3} fill="#CBD5E1" className="transition-all duration-300" />
              {/* Approved bar */}
              <rect x={x + barWidth + 2} y={padTop + usableH - approvedH} width={barWidth} height={approvedH}
                rx={3} fill="#002FA7" className="transition-all duration-300" />
              {/* Label */}
              <text x={x + barWidth} y={chartHeight - 4} textAnchor="middle"
                fontSize={9} fill="#94A3B8" fontFamily="sans-serif">{d.label}</text>
              {/* Value */}
              {d.total > 0 && (
                <text x={x + barWidth / 2} y={padTop + usableH - totalH - 4} textAnchor="middle"
                  fontSize={9} fill="#64748B" fontWeight="600" fontFamily="sans-serif">{d.total}</text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Horizontal Funnel ──
export function FunnelChart({ steps, maxValue }) {
  // steps: [{label, value, color}]
  if (!steps || steps.length === 0) return null;
  const max = maxValue || Math.max(...steps.map(s => s.value), 1);

  return (
    <div className="space-y-2" data-testid="funnel-chart">
      {steps.map((step, i) => {
        const pct = max > 0 ? (step.value / max) * 100 : 0;
        const dropoff = i > 0 && steps[i - 1].value > 0
          ? Math.round(((steps[i - 1].value - step.value) / steps[i - 1].value) * 100)
          : null;
        return (
          <div key={step.label} className="flex items-center gap-3">
            <div className="w-28 text-right text-xs text-slate-500 shrink-0 truncate">{step.label}</div>
            <div className="flex-1 relative">
              <div className="h-7 bg-slate-50 rounded-lg overflow-hidden">
                <div
                  className="h-full rounded-lg transition-all duration-700 flex items-center justify-end pr-2"
                  style={{ width: `${Math.max(pct, 4)}%`, backgroundColor: step.color }}
                >
                  <span className="text-[10px] font-semibold text-white">{step.value}</span>
                </div>
              </div>
            </div>
            <div className="w-12 text-right shrink-0">
              {dropoff !== null && dropoff > 0 && (
                <span className="text-[9px] text-red-400 font-medium">-{dropoff}%</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
